import { useContext } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ToastStatus } from '../../constants/toastMessage.constants';

import { MessageContentContext } from '../../context/EditorContext';

import { BaseError } from '../../helpers/BaseError';

import {
    MessageContentUpdate,
    MessageContentUpdateResponse,
} from '../../models/message-content.model';

import API from '../../services/api';
import { toastService } from '../../services/toast.service';

import alternateKeys from '../alternates/_queryKeys';
import formulaKeys from '../formulas/_queryKeys';
import queryKeys from './_queryKey';

const updateMessageContent = async (
    id: string,
    payload: MessageContentUpdate,
) => {
    const { data } = await API.updateMessageContent(id, payload);
    return data;
};

export default function useMessageContentUpdateMutation() {
    const queryClient = useQueryClient();

    const editorContext = useContext(MessageContentContext);

    return useMutation<
        MessageContentUpdateResponse,
        BaseError,
        MessageContentUpdate
    >({
        mutationFn: (payload) => {
            if (payload.id) {
                return updateMessageContent(payload.id, payload);
            }
            toastService.showNotificationToast(
                ToastStatus.Error,
                'Failed to update message content',
            );
            return Promise.resolve(null);
        },
        onMutate: (payload) => {
            if (payload.id && editorContext) {
                editorContext.setIsContentUpdating(true);
            }
        },
        onSuccess: (data) => {
            // invalidate the alternates query for the added variants
            const {
                added_variant_ids = [],
                removed_variant_ids = [],
                message_content,
            } = data;
            [...added_variant_ids, ...removed_variant_ids].forEach(
                (variantId) => {
                    queryClient.invalidateQueries({
                        queryKey:
                            alternateKeys.getVariantsByComponentId(variantId),
                    });
                },

                queryClient.invalidateQueries({
                    queryKey: alternateKeys.getVariantsByMessageContentId(
                        message_content.id,
                    ),
                }),
            );

            // invalidate messages query, to get the latest messages
            queryClient.invalidateQueries({
                queryKey: formulaKeys.formulaMessages(
                    message_content.formula_id,
                    message_content.id,
                ),
            });

            // invalidate the message content query to get the latest message content
            queryClient.invalidateQueries({
                queryKey: queryKeys.messageContent(data.message_content.id),
            });

            // invalidate all the message content of formula
            queryClient.invalidateQueries({
                queryKey: formulaKeys.formulaMessageContents(
                    message_content.formula_id,
                ),
            });
        },
        onError: () => {
            toastService.showNotificationToast(
                ToastStatus.Error,
                'Failed to update message content',
            );
        },
        onSettled: () => {
            if (editorContext) {
                editorContext.setIsContentUpdating(false);
            }
        },
    });
}
